import moment, { Moment } from "moment";

const SLASH_DMY = "DD/MM";
const SLASH_DMYHM = "HH:mm น.";

export const dateFormatter = (d: string, withTime?: boolean) => {
  let date = moment(d).format(SLASH_DMY) + "/" + moment(d).add(543, "year").year();
  withTime && (date = date + " " + moment(d).format(SLASH_DMYHM));
  return date;
};
export const convertBuddhistYear = {
  toBuddhistYear: (moment: Moment, format: string) => {
    const christianYear = moment.format("YYYY");
    const buddhishYear = (parseInt(christianYear) + 543).toString();
    return moment
      .format(format.replace("YYYY", buddhishYear).replace("YY", buddhishYear.substring(2, 4)))
      .replace(christianYear, buddhishYear);
  },
};

export const priceFormatter = (
  price: number | string,
  fractionDigits?: number,
  reverseUnit?: boolean,
  hideUnit?: boolean,
) => {
  const digit = !fractionDigits && fractionDigits !== 0 ? 2 : fractionDigits;
  const formatter = new Intl.NumberFormat("th-TH", {
    // style: 'currency',
    currency: "THB",
    minimumFractionDigits: digit,
    maximumFractionDigits: digit,
  });
  const num = typeof price === "number" ? price : parseFloat(price);
  const result = formatter.format(num);

  return isNaN(num)
    ? "-"
    : hideUnit
      ? `${formatter.format(num)}`
      : reverseUnit
        ? `${formatter.format(num)} ฿`
        : `฿ ${formatter.format(num)}`;
};

export const numberFormatter = (price: number | string, fractionDigits?: number) => {
  return priceFormatter(price, fractionDigits, false, true);
};

export const nameFormatter = (input: string) => {
  if (!input) return "";
  const upperCaseFirstLetter = `${input.slice(0, 1).toUpperCase().slice(1)}`;

  const lowerCaseAllWordsExceptFirstLetters = input?.replaceAll(
    /\S*/g,
    (word) => `${word?.slice(0, 1)}${word.slice(1).toLowerCase()}`,
  );

  return upperCaseFirstLetter + lowerCaseAllWordsExceptFirstLetters;
};

export const formatDate = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const thaiDate = date.toLocaleDateString("th-TH", options);
  const year = date.getFullYear();
  return thaiDate.replace(/(\d{4})/, year.toString());
};

export const formatDateRange = (startDate: Date, endDate: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
  };

  const formatWithYear = (date: Date) =>
    `${date.toLocaleDateString("th-TH", options)} ${date.getFullYear()}`;

  return `${formatWithYear(endDate)} - ${formatWithYear(startDate)}`;
};
export const formatDateYear = (date: string, isDayFormat: boolean): string => {
  const monthNames = [
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];

  const dateObj = new Date(date);
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  if (isDayFormat) {
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${day} ${month} ${year}`;
  } else {
    return `${month} ${year}`;
  }
};

/**
 * Compact K/M label for tier accrued totals: 1,500 → "1.5K฿", 1,050,000 → "1.1M฿", 125,400,000 → "125.4M฿".
 * One decimal, rounded half away from zero (5,286,726.86 → "5.3M฿", 1,250 → "1.3K฿"), ".0" dropped.
 * A K value that rounds up to 1,000K is promoted to M (999,999 → "1M฿", not "1000K฿").
 * Below 1,000 the value is shown unscaled with two decimals: 850 → "850.00฿", 12.5 → "12.50฿".
 * Returns "-" for null/undefined/NaN.
 * Non-ICPL companies count tonnes: "3,500 ตัน".
 *
 * `showUnit` (default true) — pass false to drop the whole suffix (K/M scale and ฿, or ตัน):
 * ICPL "1.5" (still divided by 1,000 / 1,000,000), others "3,500". For places that already
 * print the scale and unit elsewhere.
 */
export const compactAmountFormatter = (
  company: string,
  value?: number | null,
  showUnit = true,
): string => {
  if (company === "ICPL") {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
    const n = Number(value);
    const abs = Math.abs(n);
    if (abs < 1_000) return showUnit ? `${n.toFixed(2)}฿` : n.toFixed(2);
    // Divide by unit/10 first so the rounding works on a whole number of tenths
    // (avoids float artefacts such as 4.35 * 10 = 43.4999…). Math.round alone rounds
    // -2.5 to -2, so round the magnitude and put the sign back.
    const toTenths = (unit: number) => Math.sign(n) * Math.round(abs / (unit / 10));
    let scale = abs < 1_000_000 ? "K" : "M";
    let tenths = toTenths(scale === "K" ? 1_000 : 1_000_000);
    if (scale === "K" && Math.abs(tenths) >= 10_000) {
      scale = "M";
      tenths = toTenths(1_000_000);
    }
    return showUnit ? `${tenths / 10}${scale}฿` : `${tenths / 10}`;
  }
  const amount = numberFormatter(value || 0) ?? "-";
  return showUnit ? `${amount} ตัน` : amount;
};
